/**
 * CountryHubPage — the "magazine cover" for one country.
 *
 * Per ARCHITECTURE §6.1 layout:
 *   1. Cover    — flag + name + tagline
 *   2. Hook     — editorial intro (filled later, hub-essay placeholder for now)
 *   3. Snapshot — at-a-glance metrics
 *   4. Topic grid — 12 cards linking to topic articles
 *   5. Annual timeline — placeholder slot
 *   6. Neighbors strip — 3 same-region countries
 *   7. CTA footer — quiz, checklist, map (Italy only)
 *
 * The page is i18n-aware: language comes from URL prefix (`/ru/...`), not
 * from i18n.language alone — this keeps SEO crawlers and UI in sync.  See
 * useWikiUrlLang().
 *
 * Mobile-first.  Above the 480 px fold: flag + name + one number + CTA cue.
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useJsonLd, useSeoMeta } from '@/hooks/useSeoMeta';
import { countriesQuery, type Country } from '@/data';
import { checklistPath } from '@/features/next-steps/slugs';
import {
  ALL_COUNTRY_CODES,
  COUNTRY_SLUGS,
  TOPICS,
  buildWikiUrl,
  getLangDef,
  slugToCountryCode,
  type CountryCode,
  type LangCode,
} from '@/features/wiki/architecture';
import { PUBLISHED_COUNTRY_CODES } from '@/features/wiki/published-countries';
import {
  buildCountryHubJsonLd,
  useWikiHreflang,
} from '@/features/wiki/seo';
import { useHubArticle } from '@/features/wiki/useArticle';
import { HubBody } from '@/features/wiki/Renderer';
import { buildGuideUrl } from '@/features/guide';
import { RadarChart, type RadarDatum } from '@/components/RadarChart';
import { ConsultantCallout } from '@/components/ConsultantCallout';
import { ReportSeoCta } from '@/features/report';
import {
  computeNeutralScores,
  NEUTRAL_DIMENSIONS,
  type NeutralDimension,
} from '@/features/quiz/neutralScores';
import { useCostOfLiving, getDisplayCost } from '@/features/quiz/cost-of-living';

interface CountryHubPageProps {
  /** URL-derived language — passed from the route wrapper, not read from i18n. */
  lang: LangCode;
}

const SHIPPING_QUARTER: Record<string, { q: number; year: number }> = {
  AE: { q: 4, year: 2026 },
  PT: { q: 3, year: 2026 },
  ES: { q: 3, year: 2026 },
  CY: { q: 4, year: 2026 },
};

export default function CountryHubPage({ lang }: CountryHubPageProps) {
  useDocumentLang();
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  // Keep UI language in sync with URL language.  When a user lands on
  // /ru/countries/italy, i18n must switch to 'ru' so all UI strings match.
  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang).catch(() => {});
    }
  }, [lang, i18n]);

  // Scroll the new country hub into view when the user arrives via a
  // related-country tile or any in-app link, instead of inheriting the
  // previous page's scroll offset.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [slug]);

  const cc = useMemo<CountryCode | null>(() => slugToCountryCode(slug), [slug]);
  const { data: countriesFile, isLoading } = useQuery(countriesQuery());
  const { article: hubArticle } = useHubArticle(cc, lang);

  const country = useMemo<Country | null>(() => {
    if (!cc || !countriesFile) return null;
    return countriesFile.countries.find((c) => c.code === cc) ?? null;
  }, [cc, countriesFile]);

  const countryName = country
    ? (lang === 'ru' ? country.name_ru : country.name_en)
    : '';

  // SEO meta — driven entirely by URL + country data.
  // OG image priority: explicit plan.seo.og_image > hero illustration > none.
  // SVG heroes get rewritten to .png because Facebook/LinkedIn/Twitter don't
  // reliably render SVG previews — see scripts/rasterize-og.mjs.
  const rawOg = hubArticle?.plan.seo.og_image ?? hubArticle?.hero?.src;
  const ogImagePath = rawOg && rawOg.endsWith('.svg') ? rawOg.replace(/\.svg$/, '.png') : rawOg;
  const ogImageAbs = ogImagePath
    ? `https://lodestar.place${ogImagePath}`
    : undefined;
  useSeoMeta({
    title: country
      ? t('wiki.hub.metaTitle', { country: countryName })
      : t('wiki.notFound.title'),
    description: t('wiki.hub.metaDescription', { country: countryName }),
    canonical: buildWikiUrl({ cc: cc ?? undefined, lang, origin: 'https://lodestar.place' }),
    locale: getLangDef(lang).ogLocale,
    ogType: 'website',
    ogImage: ogImageAbs,
  });

  useWikiHreflang({ cc: cc ?? undefined });

  useJsonLd(
    country && cc
      ? buildCountryHubJsonLd({
          cc,
          lang,
          countryName,
          description: t('wiki.hub.metaDescription', { country: countryName }),
          atlasLabel: t('wiki.atlas'),
        })
      : null,
    'wiki-country-hub',
  );

  // Unknown slug → 404 to atlas.
  if (!isLoading && !cc) {
    return <NotFound lang={lang} />;
  }

  if (isLoading || !country) {
    return <HubSkeleton />;
  }

  const ship = SHIPPING_QUARTER[country.code];

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:py-12">
      {/* Breadcrumb */}
      <nav
        aria-label="breadcrumb"
        className="mb-5 font-mono text-[10px] uppercase tracking-wider text-ink-400"
      >
        <Link to={buildWikiUrl({ lang })} className="hover:text-ink-700">
          {t('wiki.atlas')}
        </Link>
        <span aria-hidden="true" className="mx-1.5">/</span>
        <span className="text-ink-700">{countryName}</span>
      </nav>

      {/* Cover (above the mobile fold) */}
      <header className="mb-10 sm:mb-12">
        <div className="flex items-start gap-3 sm:items-center sm:gap-5">
          <span
            aria-hidden="true"
            className="text-[64px] leading-none sm:text-[96px]"
          >
            {country.flag_emoji}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[11px] uppercase tracking-wider text-ink-500">
              {country.region} · {country.code}
              {ship && (
                <span className="ml-2 rounded-full border border-terra-600/40 px-2 py-0.5 text-[10px] text-terra-600">
                  {t('countries.shipping.quarter', { q: ship.q, year: ship.year })}
                </span>
              )}
            </p>
            <h1 className="mt-1 font-display text-[clamp(28px,6vw,40px)] font-medium italic tracking-tight text-ink-900">
              {countryName}
            </h1>
            <p className="mt-1 text-sm text-ink-500 sm:text-base">
              {t('wiki.hub.tagline')}
            </p>
          </div>
        </div>

        {/* Above-fold key number — see ARCHITECTURE §4.3 */}
        <FoldKeyNumber country={country} t={t} />
      </header>

      {/* Cover essay — real content when a HubArticle exists, otherwise the
          author-facing placeholder so unfinished hubs are clearly draft. */}
      {hubArticle ? (
        <HubBody article={hubArticle} cc={country.code as CountryCode} lang={lang} />
      ) : (
        <section
          aria-labelledby="hub-essay-title"
          className="mb-12 rounded-lg border border-dashed border-ink-100 bg-paper-2/60 p-5 text-sm text-ink-500"
        >
          <p className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
            {t('wiki.hub.draftBadge')}
          </p>
          <p className="mt-2">{t('wiki.hub.comingSoonContent')}</p>
        </section>
      )}

      {/* Snapshot */}
      <SnapshotCard country={country} lang={lang} t={t} />

      {/* Topics grid */}
      <section aria-labelledby="topics-title" className="mt-12">
        <h2
          id="topics-title"
          className="font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl"
        >
          {t('wiki.hub.topicsTitle')}
        </h2>
        <p className="mt-1 text-sm text-ink-500">{t('wiki.hub.topicsIntro')}</p>

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TOPICS.map((topic) => (
            <TopicCard
              key={topic.slug}
              cc={country.code as CountryCode}
              slug={topic.slug}
              icon={topic.icon}
              label={t(`wiki.topic.${topic.i18nKey}.label`)}
              description={t(`wiki.topic.${topic.i18nKey}.description`)}
              lang={lang}
            />
          ))}
        </div>
      </section>

      {/* Annual timeline slot — real widget plugs in here */}
      <section aria-labelledby="annual-title" className="mt-12">
        <h2
          id="annual-title"
          className="font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl"
        >
          {t('wiki.hub.annualTitle', { country: countryName })}
        </h2>
        <p className="mt-1 text-sm text-ink-500">{t('wiki.hub.annualIntro')}</p>
        <div className="mt-4 rounded-lg border border-dashed border-ink-100 bg-paper-2/60 p-5 text-sm text-ink-500">
          {t('wiki.hub.comingSoonContent')}
        </div>
      </section>

      {/* Compare with other published guides */}
      {countriesFile && (
        <PublishedGuidesStrip
          countries={countriesFile.countries}
          current={country}
          lang={lang}
          t={t}
        />
      )}

      {/* Neighbours */}
      {countriesFile && (
        <NeighboursStrip
          countries={countriesFile.countries}
          current={country}
          lang={lang}
          t={t}
        />
      )}

      {/* CTA footer */}
      <footer className="mt-14 border-t border-ink-100 pt-6">
        <div className="flex flex-wrap gap-3">
          <Link
            to="/quiz"
            className="inline-flex h-11 items-center rounded-md bg-terra-600 px-5 text-sm font-medium text-paper transition-colors hover:bg-terra-700"
          >
            {t('wiki.hub.ctaQuiz')}
          </Link>
          <Link
            to={checklistPath(country.code)}
            className="inline-flex h-11 items-center rounded-md border border-ink-100 px-5 text-sm font-medium text-ink-700 transition-colors hover:bg-paper-2"
          >
            {t('wiki.hub.ctaChecklist')}
          </Link>
          {country.code === 'IT' && (
            <Link
              to="/it/map"
              className="inline-flex h-11 items-center rounded-md border border-ink-100 px-5 text-sm font-medium text-ink-700 transition-colors hover:bg-paper-2"
            >
              {t('wiki.hub.ctaMap')}
            </Link>
          )}
        </div>
        <Link
          to={buildGuideUrl({ lang })}
          className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-ink-500 hover:text-ink-900"
        >
          <span aria-hidden="true">{'◇'}</span>
          <span>{t('wiki.hub.ctaGuide', { defaultValue: 'How to use the map →' })}</span>
        </Link>
        <ReportSeoCta
          countryName={lang === 'ru' ? country.name_ru : country.name_en}
          lang={lang === 'ru' ? 'ru' : 'en'}
          className="mt-6"
        />
        <ConsultantCallout source="country_hub" variant="inline" className="mt-6" country={country.code} />
      </footer>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Sub-components                                                     */
/* ────────────────────────────────────────────────────────────────── */

function FoldKeyNumber({
  country,
  t,
}: {
  country: Country;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  // The hub has no quiz answers, so it shows the single-person basket and
  // names it. It used to show `metrics.cost.monthly_eur_family`, a fabricated
  // figure labelled "small family" that every visitor saw regardless of
  // household. See docs/data/COST_OF_LIVING_SCHEMA.md.
  const { data: costFile } = useCostOfLiving();
  const display = getDisplayCost(costFile, country.code);
  const cost = display?.amount;
  const taxTop = country.metrics?.tax.pit_top_rate_pct;
  if (cost == null && taxTop == null) return null;

  return (
    <dl className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {cost != null && (
        <Stat
          label={t('countries.metrics.costLabel')}
          value={`€${cost.toLocaleString('en-US')}`}
          hint={`${t('countries.metrics.costIn', {
            household: t(`countries.metrics.costHousehold.${display!.bucket}`),
            city: display!.city,
          })}. ${t('countries.metrics.costHelper')}`}
        />
      )}
      {taxTop != null && (
        <Stat
          label={t('countries.metrics.taxLabel')}
          value={`${taxTop}%`}
          hint={t('countries.metrics.taxHelper')}
        />
      )}
      {country.metrics?.climate.jul_max_c != null && (
        <Stat label="Jul" value={`${country.metrics.climate.jul_max_c}°C`} />
      )}
      {country.metrics?.climate.jan_min_c != null && (
        <Stat label="Jan" value={`${country.metrics.climate.jan_min_c}°C`} />
      )}
    </dl>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md border border-ink-100 bg-paper-2/60 px-3 py-2.5">
      <dt
        className="font-mono text-[10px] uppercase tracking-wider text-ink-400"
        title={hint}
      >
        {label}
      </dt>
      <dd className="mt-0.5 font-display text-lg font-medium italic tabular-nums text-ink-900 sm:text-xl">
        {value}
      </dd>
    </div>
  );
}

function SnapshotCard({
  country,
  lang,
  t,
}: {
  country: Country;
  lang: LangCode;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  void lang;
  const { data: costFile } = useCostOfLiving();

  // Build radar data only when metrics are available.
  const radarData: RadarDatum[] | null = useMemo(() => {
    if (!country.metrics) return null;
    const scores = computeNeutralScores(country.metrics, costFile, country.code);
    return NEUTRAL_DIMENSIONS.map((dim: NeutralDimension) => ({
      key: dim,
      label: t(`quiz.result.dim.${dim}`),
      value: scores[dim],
    }));
  }, [country.metrics, country.code, costFile, t]);

  return (
    <section aria-labelledby="snapshot-title">
      <h2
        id="snapshot-title"
        className="font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl"
      >
        {t('wiki.hub.snapshotTitle')}
      </h2>
      <p className="mt-1 text-sm text-ink-500">{t('wiki.hub.snapshotIntro')}</p>

      {radarData ? (
        <div className="mt-4 flex justify-center rounded-lg border border-ink-100 bg-paper-2 p-4 text-terra-600">
          <RadarChart data={radarData} size={240} />
        </div>
      ) : (
        /* Fallback for countries without metrics */
        <div className="mt-4 rounded-lg border border-ink-100 bg-paper-2 p-5 text-sm text-ink-500">
          {t('wiki.hub.comingSoonContent')}
        </div>
      )}
    </section>
  );
}

function TopicCard({
  cc,
  slug,
  icon,
  label,
  description,
  lang,
}: {
  cc: CountryCode;
  slug: string;
  icon: string;
  label: string;
  description: string;
  lang: LangCode;
}) {
  const href = buildWikiUrl({ cc, topic: slug as never, lang });
  return (
    <Link
      to={href}
      className="group flex min-h-[88px] items-start gap-3 rounded-lg border border-ink-100 bg-paper-2/60 p-4 transition-colors hover:bg-paper-2 hover:shadow-sm"
    >
      <span aria-hidden="true" className="text-2xl leading-none">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block font-display text-base font-medium italic text-ink-900 group-hover:text-terra-700">
          {label}
        </span>
        <span className="mt-0.5 block text-xs text-ink-500">{description}</span>
      </span>
    </Link>
  );
}

function PublishedGuidesStrip({
  countries,
  current,
  lang,
  t,
}: {
  countries: Country[];
  current: Country;
  lang: LangCode;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const others = PUBLISHED_COUNTRY_CODES.filter((cc) => cc !== current.code);
  if (others.length === 0) return null;
  const otherCountries = others
    .map((cc) => countries.find((c) => c.code === cc))
    .filter(Boolean) as Country[];

  return (
    <section aria-labelledby="published-guides-title" className="mt-12">
      <h2
        id="published-guides-title"
        className="font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl"
      >
        {t('wiki.hub.publishedGuidesTitle')}
      </h2>
      <p className="mt-1 text-sm text-ink-500">{t('wiki.hub.publishedGuidesIntro')}</p>
      <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {otherCountries.map((c) => {
          const cc = c.code as CountryCode;
          return (
            <li key={cc}>
              <Link
                to={buildWikiUrl({ cc, lang })}
                className="group flex items-center gap-3 rounded-md border border-ink-100 bg-paper-2/60 px-4 py-3 transition-colors hover:bg-paper-2"
              >
                <span aria-hidden="true" className="text-2xl leading-none">
                  {c.flag_emoji}
                </span>
                <span className="min-w-0">
                  <span className="block font-display text-base font-medium italic text-ink-900 group-hover:text-terra-700">
                    {lang === 'ru' ? c.name_ru : c.name_en}
                  </span>
                  <span className="block font-mono text-[10px] uppercase tracking-wider text-ink-400">
                    {t('wiki.hub.topicsTitle')} ·{' '}
                    {TOPICS.length} {lang === 'ru' ? 'глав' : 'chapters'}
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function NeighboursStrip({
  countries,
  current,
  lang,
  t,
}: {
  countries: Country[];
  current: Country;
  lang: LangCode;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const neighbours = countries
    .filter((c) => c.region === current.region && c.code !== current.code)
    .slice(0, 3);
  if (neighbours.length === 0) return null;

  return (
    <section aria-labelledby="neighbours-title" className="mt-12">
      <h2
        id="neighbours-title"
        className="font-display text-xl font-medium italic tracking-tight text-ink-900 sm:text-2xl"
      >
        {t('wiki.hub.neighborsTitle')}
      </h2>
      <p className="mt-1 text-sm text-ink-500">{t('wiki.hub.neighborsIntro')}</p>
      <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {neighbours.map((c) => (
          <li key={c.code}>
            <Link
              to={buildWikiUrl({ cc: c.code as CountryCode, lang })}
              className="flex items-center gap-3 rounded-md border border-ink-100 bg-paper-2/60 px-4 py-3 transition-colors hover:bg-paper-2"
            >
              <span aria-hidden="true" className="text-2xl leading-none">
                {c.flag_emoji}
              </span>
              <span>
                <span className="block font-display text-base font-medium italic text-ink-900">
                  {lang === 'ru' ? c.name_ru : c.name_en}
                </span>
                <span className="block font-mono text-[10px] uppercase tracking-wider text-ink-400">
                  {c.tier}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function HubSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="mx-auto w-full max-w-5xl animate-pulse px-4 py-12"
    >
      <div className="mb-6 h-3 w-24 rounded bg-ink-100" />
      <div className="mb-2 h-12 w-2/3 rounded bg-ink-100" />
      <div className="mb-10 h-4 w-1/2 rounded bg-ink-100" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-24 rounded-lg bg-ink-100" />
        ))}
      </div>
    </div>
  );
}

function NotFound({ lang }: { lang: LangCode }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-medium italic tracking-tight text-ink-900">
        {t('wiki.notFound.title')}
      </h1>
      <p className="mt-3 text-sm text-ink-500">{t('wiki.notFound.subtitle')}</p>
      <Link
        to={buildWikiUrl({ lang })}
        className="mt-6 inline-flex h-11 items-center rounded-md bg-terra-600 px-5 text-sm font-medium text-paper hover:bg-terra-700"
      >
        {t('wiki.notFound.ctaAtlas')}
      </Link>
    </div>
  );
}

// Type-safety check: assert that we know all 30 country codes at build time.
// Removing or renaming a country slug elsewhere will trip this.
void ALL_COUNTRY_CODES;
void COUNTRY_SLUGS;
void Navigate;
